import {
  useRepositoryOwnershipHandler,
  useGenericOwnershipHandler,
  useJiraTeamOwnershipHandler,
  useSubscriptionOwnershipHandler,
  usePagerDutyTeamOwnershipHandler,
} from './handlers';
import { OwnershipHandlerConfig } from './types';

/**
 * Explicit configuration mapping entity kinds to their ownership handlers.
 * Every supported entity kind must be registered here.
 * There are no defaults - unregistered kinds will throw an error.
 */
export const ownershipHandlerConfig: Record<string, OwnershipHandlerConfig> = {
  Repository: {
    handler: useRepositoryOwnershipHandler,
    entityLabel: 'Repository',
    description: 'Updates via engineering-insights API and refreshes entity',
  },
  Resource: {
    handler: useGenericOwnershipHandler,
    entityLabel: 'Resource',
    description: 'Updates via accentuate API',
  },
  Component: {
    handler: useGenericOwnershipHandler,
    entityLabel: 'Component',
    description: 'Updates via accentuate API',
  },
  'jira-team': {
    handler: useJiraTeamOwnershipHandler,
    entityLabel: 'Jira Team',
    description: 'Updates via Watchtower API',
  },
  subscription: {
    handler: useSubscriptionOwnershipHandler,
    entityLabel: 'Subscription',
    description: 'Updates via Watchtower API and refreshes entities',
  },
  'pagerduty-team': {
    handler: usePagerDutyTeamOwnershipHandler,
    entityLabel: 'PagerDuty Team',
    description: 'Updates via Watchtower API',
  },
  API: {
    handler: useGenericOwnershipHandler,
    entityLabel: 'API',
    description: 'Updates via accentuate API',
  },
  System: {
    handler: useGenericOwnershipHandler,
    entityLabel: 'System',
    description: 'Updates via accentuate API',
  },
  Domain: {
    handler: useGenericOwnershipHandler,
    entityLabel: 'Domain',
    description: 'Updates via accentuate API',
  },
};

/**
 * Get the handler configuration for a specific entity kind.
 * @throws Error if the kind is not registered in the config
 */
export function getHandlerForKind(kind: string): OwnershipHandlerConfig {
  const handler = ownershipHandlerConfig[kind];
  if (!handler) {
    throw new Error(
      `No ownership handler configured for entity kind: "${kind}". ` +
        `Please add a handler configuration in ManageOwnership/config.ts`,
    );
  }
  return handler;
}

/**
 * Check if a handler is registered for a given entity kind.
 */
export function hasHandlerForKind(kind: string): boolean {
  return kind in ownershipHandlerConfig;
}

/**
 * Get all registered entity kinds.
 */
export function getRegisteredKinds(): string[] {
  return Object.keys(ownershipHandlerConfig);
}
